import { ArrowDownward, ArrowUpward, Delete, DragIndicator, Warning } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import { useEffect, useState } from "react";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: 'white',
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const Sub = styled(Paper)(({ theme }) => ({
    backgroundColor: 'transparent',
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const MAX_LABEL_LENGTH = 48;
function trimLabel(text: string): string {
    if (text.length <= MAX_LABEL_LENGTH) return text;
    return text.slice(0, MAX_LABEL_LENGTH).trim() + "\u2026";
}




const SortableFields = ({
    fields,
    onFieldChange: onFieldChange,
    onSortChange: onSortChange,
    fieldWarnings,
    onRemoveField
}: {
    fields: any,
    onFieldChange: Function,
    onSortChange: Function,
    fieldWarnings?: Map<string, string>,
    onRemoveField?: (field: any) => void
}) => {

    const [sortedFields, setSortedFields] = useState<any[]>([])

    const handleChange = (field: any) => {
        onFieldChange(field)
    }

    useEffect(() => {
        // Create a copy to avoid mutating props
        const fieldsWithOrder = fields.map((field: any, index: number) => {
            if (!field.order && field.order !== 0) {
                return { ...field, order: index };
            }
            return { ...field };
        });

        const sorted = [...fieldsWithOrder].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
        setSortedFields(sorted);
    }, [fields])

    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.setData('application/json', JSON.stringify({ index }));
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        setDropTargetIndex(index);
    };

    const clearDropTarget = () => {
        setDropTargetIndex(null);
        setDraggedIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        e.stopPropagation();
        const sourceIndex = draggedIndex ?? (e.dataTransfer.getData('text/plain') ? parseInt(e.dataTransfer.getData('text/plain'), 10) : null);
        if (sourceIndex === null || sourceIndex === dropIndex) {
            clearDropTarget();
            return;
        }

        let fieldsCopy = [...sortedFields].filter((field: any) => typeof (field) !== 'undefined' && field);
        let [movedObject] = fieldsCopy.splice(sourceIndex, 1);
        fieldsCopy.splice(dropIndex, 0, movedObject);

        const withOrder = fieldsCopy.map((f: any, i: number) => (f ? { ...f, order: i } : f)).filter(Boolean);
        setSortedFields(withOrder);
        onSortChange(withOrder);
        clearDropTarget();
    };

    const handleDragEnd = () => {
        clearDropTarget();
    };

    const handleDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDropTargetIndex(null);
        }
    };

    const handleMove = (index: number, direction: -1 | 1) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= sortedFields.length) return;
        const fieldsCopy = [...sortedFields];
        const [moved] = fieldsCopy.splice(index, 1);
        fieldsCopy.splice(newIndex, 0, moved);
        const withOrder = fieldsCopy.map((f: any, i: number) => (f ? { ...f, order: i } : f)).filter(Boolean);
        setSortedFields(withOrder);
        onSortChange(withOrder);
    };

    return (
        <Stack
            spacing={1.5}
            direction={'column'}
            alignItems={'flex-start'}
            sx={{ width: '100%' }}
            onDragLeave={handleDragLeave}
        >
            {sortedFields.length === 0 && (
                <div className="text-sm text-gray-500 py-2">No fields</div>
            )}
            {(sortedFields || []).map((field: any, index: number) => {
                return (field && (
                    <Stack
                        direction={'column'}
                        key={field.uid ?? field.name ?? index}
                        sx={{ width: '100%' }}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                    >
                        {dropTargetIndex === index && (
                            <div
                                style={{
                                    height: '3px',
                                    backgroundColor: '#2563eb',
                                    borderRadius: 2,
                                    margin: '-2px 0 2px 0',
                                    flexShrink: 0,
                                }}
                                aria-hidden
                            />
                        )}
                        <Stack direction={'row'} sx={{ width: '100%' }}>
                        <Item
                            className="cursor-pointer flex-1"
                            onClick={() => handleChange(field)}
                            data-testid="field-list-row"
                            sx={{
                                backgroundColor: fieldWarnings?.has(field?.name) ? '#fffbeb' : '#ffffff',
                                border: fieldWarnings?.has(field?.name) ? '1px solid #fbbf24' : '1px solid #e5e7eb',
                                padding: '8px 12px',
                                fontSize: '0.875rem',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                '&:hover': {
                                    backgroundColor: fieldWarnings?.has(field?.name) ? '#fef3c7' : '#f3f4f6',
                                    borderColor: fieldWarnings?.has(field?.name) ? '#f59e0b' : '#d1d5db'
                                }
                            }}
                        >
                            {fieldWarnings?.has(field?.name) && (
                                <Tooltip title={fieldWarnings.get(field?.name)} arrow placement="top">
                                    <Warning sx={{ color: '#f59e0b', fontSize: '18px', flexShrink: 0 }} />
                                </Tooltip>
                            )}
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {(() => {
                                    const label = typeof field?.label === 'string' ? field.label.trim() : '';
                                    const name = typeof field?.name === 'string' ? field.name.trim() : '';
                                    const type = typeof field?.type === 'string' ? field.type.trim() : '';
                                    let display: string;
                                    if (type.toLowerCase() === 'instruction') {
                                        display = name || "Unnamed field";
                                    } else {
                                        const labelLooksLikeType =
                                            label !== '' &&
                                            type !== '' &&
                                            label.toLowerCase() === type.toLowerCase();
                                        if (label && !labelLooksLikeType) {
                                            display = label;
                                        } else if (name) {
                                            display = name;
                                        } else if (label) {
                                            display = label;
                                        } else {
                                            display = "Unnamed field";
                                        }
                                    }
                                    const trimmed = trimLabel(display);
                                    if (trimmed !== display) {
                                        return (
                                            <Tooltip title={display} arrow placement="top">
                                                <span>{trimmed}</span>
                                            </Tooltip>
                                        );
                                    }
                                    return trimmed;
                                })()}
                            </span>
                        </Item>
                        <Sub sx={{ display: 'flex', alignItems: 'center', padding: 0 }} onMouseDown={(e) => e.stopPropagation()}>
                            <Tooltip title="Move up" arrow placement="top">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleMove(index, -1)}
                                        disabled={index === 0}
                                        sx={{ color: '#6b7280', '&:hover': { color: '#374151' }, '&.Mui-disabled': { color: '#d1d5db' } }}
                                    >
                                        <ArrowUpward sx={{ fontSize: '18px' }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Move down" arrow placement="top">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleMove(index, 1)}
                                        disabled={index === sortedFields.length - 1}
                                        sx={{ color: '#6b7280', '&:hover': { color: '#374151' }, '&.Mui-disabled': { color: '#d1d5db' } }}
                                    >
                                        <ArrowDownward sx={{ fontSize: '18px' }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {onRemoveField && (
                                <Tooltip title="Remove from form" arrow placement="top">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemoveField(field)}
                                            sx={{ color: '#6b7280', '&:hover': { color: '#dc2626' } }}
                                            aria-label="Remove field from form"
                                        >
                                            <Delete sx={{ fontSize: '18px' }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Sub>
                        <Sub
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'grab',
                                padding: '4px 8px',
                                userSelect: 'none',
                                '&:active': {
                                    cursor: 'grabbing'
                                },
                                '&:hover': {
                                    backgroundColor: '#f3f4f6'
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <DragIndicator
                                sx={{
                                    color: '#6b7280',
                                    fontSize: '20px',
                                    '&:hover': {
                                        color: '#374151'
                                    }
                                }}
                            />
                        </Sub>
                        </Stack>
                    </Stack>

                ))
            })}

        </Stack>
    )
};


export default SortableFields;